import { FormEvent, useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import isEqual from "lodash/isEqual";
import { DEFAULT_AXNUI_CONTENT } from "@iddataweb/idw-common-ui/constants";
import { ButtonTypes, LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { MANAGEMENT_CONSOLE_MASTER_DATA_WORKFLOW_TEMPLATES_URL } from "@constants/routes";
import { AXNUI_CONTENT_PROPERTY_TYPE_ID } from "@constants/verification-service-details-constants";
import { ModalTypes } from "@enums/modal-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { WorkflowTemplateDetailsTabTypes } from "@enums/workflow-template-details-tab-types";
import { useDiscardChangesModal } from "@hooks/use-discard-changes-modal";
import { SelectOption } from "@idw-types/common";
import {
    ApAssertion,
    Assertion,
    AttributeClassification,
    AttributeLabel,
    AttributeType,
    DeployedPolicyComponent,
    FallbackServiceMapping,
    RpServiceAttributeProperty,
    RpServiceAttributePropertyMap,
    ServiceOffering
} from "@idw-types/data/reference-data/generic";
import {
    AxnAttributeTypeField,
    AxnAvailablePropertyCategory,
    AxnCompany,
    AxnIdentityProvider,
    AxnRelyingParty,
    AxnServiceOfferingDataSource
} from "@idw-types/data/reference-data/axn";
import { FormattedWorkflowTemplateDetails } from "@idw-types/data/workflow-template-details";
import { updateWorkflowTemplateReduxRequest } from "@redux-requests/workflow-template-details";
import { sortStrings } from "@utils/string-util";
import { navigateToUtil } from "@utils/navigation-util";
import { extractContentValuesFromProfileProperties } from "@utils/content-util";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { WorkflowVisualizationContainer } from "@containers/common/workflow-visualization-container/workflow-visualization-container";
import { WorkflowTemplateMetadataContainer } from "./workflow-template-metadata-container/workflow-template-metadata-container";
import { TabContainer } from "@containers/common/tab-container/tab-container";
import styles from "./workflow-template-details-component.less";
import { mapEntityWithNameAndIdToSelectOption } from "@utils/select-option-mapping-util";

type WorkflowTemplateDetailsComponentProps = {
    workflowTemplateDetails: FormattedWorkflowTemplateDetails;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnCompanies: Array<AxnCompany>;
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>;
    attributeTypes: Array<AttributeType>;
    attributeLabels: Array<AttributeLabel>;
    attributeClassifications: Array<AttributeClassification>;
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>;
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>;
    axnuiContent: AXNUIContent;
    deployedPolicyComponents: Array<DeployedPolicyComponent>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    serviceOfferings: Array<ServiceOffering>;
    apAssertions: Array<ApAssertion>;
    assertions: Array<Assertion>;
    serviceOfferingDataSources: Array<AxnServiceOfferingDataSource>;
    fallbackServiceMappings: Array<FallbackServiceMapping>;
};

export type WorkflowTemplateDetailsComponentState = {
    workflowTemplate: FormattedWorkflowTemplateDetails;
    selectedOrganizationOption: SelectOption;
};

export const WorkflowTemplateDetailsComponent = (props: WorkflowTemplateDetailsComponentProps) => {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const dispatch = useDispatch();

    const {
        workflowTemplateDetails,
        axnIdentityProviders,
        axnRelyingParties,
        axnCompanies,
        axnAvailablePropertyCategories,
        attributeTypes,
        attributeLabels,
        attributeClassifications,
        rpServiceAttributeProperties,
        rpServiceAttributePropertyMaps,
        axnuiContent,
        deployedPolicyComponents,
        axnAttributeTypeFields,
        serviceOfferings,
        apAssertions,
        assertions,
        serviceOfferingDataSources,
        fallbackServiceMappings
    } = props;

    const [requestInProgress, setRequestInProgress] = useState(false);
    const [labelWidth] = useState(LabelWidthTypes.SMALL);
    const [, setIsMultiLanguageEnabled] = useState(false);

    const [state, setState] = useState<WorkflowTemplateDetailsComponentState>({
        workflowTemplate: workflowTemplateDetails,
        selectedOrganizationOption: mapEntityWithNameAndIdToSelectOption(workflowTemplateDetails.organization)
    });

    const stateIsUnchanged = useMemo(() => {
        return isEqual(workflowTemplateDetails, state.workflowTemplate);
    }, [workflowTemplateDetails, state.workflowTemplate]);

    useDiscardChangesModal({
        modalIdPrefix: "discard-workflow-template-changes",
        modalType: ModalTypes.DiscardWorkflowTemplateChanges,
        stateIsUnchanged
    });

    const sortedServices = useMemo(() => {
        if (workflowTemplateDetails.template?.rpServices && workflowTemplateDetails.template?.workflow?.rpServiceWorkflows) {
            const primaryService = workflowTemplateDetails.template.workflow.rpServiceWorkflows.find(rps => rps.primaryServiceFlag);

            const sortedRps = [...workflowTemplateDetails.template.rpServices].sort((a, b) => sortStrings(a.serviceName, b.serviceName));
            const primaryServiceIndex = sortedRps.findIndex(rps => rps.id === `${primaryService.id}`);
            if (primaryServiceIndex > 0) {
                const removedItems = sortedRps.splice(primaryServiceIndex, 1);
                sortedRps.unshift(...removedItems);
            }

            return sortedRps;
        }
        return null;
    }, [workflowTemplateDetails.template?.rpServices]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);
        const updateWorkflowTemplateDetailsResult = await updateWorkflowTemplateReduxRequest(dispatch)(
            state.workflowTemplate,
            axnIdentityProviders,
            axnRelyingParties,
            axnCompanies,
            axnAvailablePropertyCategories,
            attributeTypes,
            attributeLabels,
            attributeClassifications,
            rpServiceAttributeProperties,
            rpServiceAttributePropertyMaps,
            axnuiContent,
            deployedPolicyComponents,
            axnAttributeTypeFields,
            serviceOfferings,
            apAssertions,
            assertions,
            serviceOfferingDataSources,
            fallbackServiceMappings
        );
        if (updateWorkflowTemplateDetailsResult) {
            setState({
                ...state,
                workflowTemplate: updateWorkflowTemplateDetailsResult,
                selectedOrganizationOption: mapEntityWithNameAndIdToSelectOption(updateWorkflowTemplateDetailsResult.organization)
            });
        }
        setRequestInProgress(false);
    };

    const onCloseClick = () => {
        navigateToUtil(MANAGEMENT_CONSOLE_MASTER_DATA_WORKFLOW_TEMPLATES_URL, navigate, routerLocation.pathname);
    };

    const resource = resources.pages.workflowTemplateDetails;

    const {
        availableLanguages: availableAXNUILanguages
    } = useMemo(() => {
        return extractContentValuesFromProfileProperties(
            state.workflowTemplate?.template.appliedProfiles,
            AXNUI_CONTENT_PROPERTY_TYPE_ID,
            DEFAULT_AXNUI_CONTENT,
            LocaleTypes.ENGLISH
        );
    }, [state.workflowTemplate?.template.appliedProfiles]);

    useEffect(() => {
        if (availableAXNUILanguages) {
            const combinedLanguages = Array.from(new Set([...availableAXNUILanguages]));
            if (combinedLanguages.length > 0) {
                setIsMultiLanguageEnabled(true);

            }
        }
    }, [availableAXNUILanguages]);

    

    return (
        <div className={styles.workflowTemplateDetailsComponent}>
            <Form onSubmit={handleSubmit}>
                <div className={styles.headerContainer}>
                    <div className={styles.headerRow}>
                        <span className={styles.workflowTemplateName}>{state.workflowTemplate.name}</span>

                        <div className={styles.buttonContainer}>
                            <Button id="workflow-template-details-close-button" onClick={onCloseClick} variant={"outline-secondary"}>{resources.common.buttons.close.label}</Button>
                            <ButtonWithSpinner
                                id="workflow-template-details-save-button"
                                disabled={requestInProgress || stateIsUnchanged}
                                hideSpinner={stateIsUnchanged}
                                variant="primary"
                                type={ButtonTypes.SUBMIT}
                                label={resources.common.buttons.save.label}
                            />
                        </div>
                    </div>
                </div>
                <TabContainer>
                    {/* @ts-ignore */}
                    <Tabs id="workflow-template-details" fill={false} unmountOnExit={true}>
                        <Tab eventKey={WorkflowTemplateDetailsTabTypes.METADATA} title={resource.tabs.metadata.tabName}>
                            <ContentPageHeader title={resource.tabs.metadata.header} />
                            <ContentPageBody isMaxWidth={true}>
                                <WorkflowTemplateMetadataContainer
                                    isDisabled={requestInProgress}
                                    state={state}
                                    setState={setState}
                                    labelWidth={labelWidth}
                                    axnIdentityProviders={axnIdentityProviders}
                                    axnRelyingParties={axnRelyingParties}
                                    axnCompanies={axnCompanies}
                                    axnAvailablePropertyCategories={axnAvailablePropertyCategories}
                                    attributeTypes={attributeTypes}
                                    attributeLabels={attributeLabels}
                                    attributeClassifications={attributeClassifications}
                                    rpServiceAttributeProperties={rpServiceAttributeProperties}
                                    rpServiceAttributePropertyMaps={rpServiceAttributePropertyMaps}
                                    axnuiContent={axnuiContent}
                                    deployedPolicyComponents={deployedPolicyComponents}
                                    axnAttributeTypeFields={axnAttributeTypeFields}
                                    serviceOfferings={serviceOfferings}
                                    apAssertions={apAssertions}
                                    assertions={assertions}
                                    serviceOfferingDataSources={serviceOfferingDataSources}
                                    fallbackServiceMappings={fallbackServiceMappings}
                                />
                            </ContentPageBody>


                        </Tab>
                        {sortedServices && (
                            <Tab eventKey={WorkflowTemplateDetailsTabTypes.VISUALIZATION} title={resource.tabs.policyConfigs.tabName}>
                                <div>
                                    <div className={styles.description}>{resource.tabs.policyConfigs.description}</div>
                                    <WorkflowVisualizationContainer
                                        rpServices={sortedServices}
                                        availableLanguages={availableAXNUILanguages}
                                        hasUnsubmittedChangeRequest={false}
                                        setRpServiceConfigurationsState={() => { }}
                                        setWorkflowMessagingTemplateCache={() => {}}
                                        workflowMessagingTemplateCache={{}}
                                        primaryRpServiceId={null}
                                        onClickArchiveService={null}
                                        onClickSetAsPrimaryService={null}
                                        workflowChangeRequests={[]}
                                        generateStartChangeRequestButton={() => null}
                                        selectedLanguage={LocaleTypes.ENGLISH}
                                    />
                                </div>
                            </Tab>
                        )}
                    </Tabs>
                </TabContainer>
            </Form>
        </div>
    );
};